import { Plus, Trash2 } from 'lucide-react';

export interface LeaveTypeRow {
  name: string;
  allocated: number | '';
}

export interface PolicyPeriod {
  _id?: string;
  fromDate: string;
  toDate: string;
  leaveTypes: LeaveTypeRow[];
}

const normalizeLeaveTypeName = (name: string) =>
  name.trim().replace(/\s+/g, ' ').replace(/\s+leave$/i, '').trim();

export const hasStarted = (p: PolicyPeriod) => {
  const today = new Date().toISOString().slice(0, 10);
  return !!p._id && p.fromDate <= today;
};

export const toPolicyPeriods = (
  policies?: { _id?: string; fromDate: string; toDate: string; leaveTypes: Record<string, { allocated: number }> }[]
): PolicyPeriod[] =>
  (policies ?? []).map((p) => ({
    _id: p._id,
    fromDate: p.fromDate.slice(0, 10),
    toDate: p.toDate.slice(0, 10),
    leaveTypes: Object.entries(p.leaveTypes ?? {}).map(([name, { allocated }]) => ({
      name: normalizeLeaveTypeName(name),
      allocated,
    })),
  }));

export const fromPolicyPeriods = (
  periods: PolicyPeriod[]
): { _id?: string; fromDate: string; toDate: string; leaveTypes: Record<string, { allocated: number }> }[] => {
  const sorted = [...periods].sort((a, b) => a.fromDate.localeCompare(b.fromDate));
  return sorted.map((p, i) => {
    let toDate = p.toDate;
    if (i < sorted.length - 1) {
      const nextFrom = new Date(sorted[i + 1].fromDate);
      nextFrom.setUTCDate(nextFrom.getUTCDate() - 1);
      toDate = nextFrom.toISOString().slice(0, 10);
    }
    return {
      _id: p._id,
      fromDate: p.fromDate,
      toDate,
      leaveTypes: p.leaveTypes.reduce(
        (acc, { name, allocated }) => {
          const normalizedName = normalizeLeaveTypeName(name);
          if (normalizedName) {
            acc[normalizedName] = {
              allocated: allocated === '' ? 0 : (allocated as number),
            };
          }
          return acc;
        },
        {} as Record<string, { allocated: number }>
      ),
    };
  });
};

export const emptyPolicy = (): PolicyPeriod => {
  const today = new Date();
  const startOfYear = new Date(Date.UTC(today.getFullYear(), 0, 1));
  const endOfYear = new Date(Date.UTC(today.getFullYear(), 11, 31));
  return {
    fromDate: startOfYear.toISOString().slice(0, 10),
    toDate: endOfYear.toISOString().slice(0, 10),
    leaveTypes: [{ name: '', allocated: '' }],
  };
};

interface LeavePoliciesEditorProps {
  label: string;
  policies: PolicyPeriod[];
  onChange: (policies: PolicyPeriod[]) => void;
  inputClassName: string;
  labelClassName: string;
  isDarkMode: boolean;
}

const LeavePoliciesEditor: React.FC<LeavePoliciesEditorProps> = ({
  label,
  policies,
  onChange,
  inputClassName,
  labelClassName,
  isDarkMode,
}) => {
  const updatePolicy = (index: number, patch: Partial<PolicyPeriod>) => {
    const next = [...policies];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const updateLeaveType = (pIdx: number, ltIdx: number, patch: Partial<LeaveTypeRow>) => {
    const next = [...policies];
    const types = [...next[pIdx].leaveTypes];
    types[ltIdx] = { ...types[ltIdx], ...patch };
    next[pIdx] = { ...next[pIdx], leaveTypes: types };
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {label}
        </h3>
        <button
          type="button"
          onClick={() => {
            const newPolicy = emptyPolicy();
            if (policies.length > 0) {
              const last = policies[policies.length - 1];
              const nextStart = new Date(`${last.toDate}T00:00:00.000Z`);
              nextStart.setUTCDate(nextStart.getUTCDate() + 1);
              const nextStartStr = nextStart.toISOString().slice(0, 10);
              const nextEnd = new Date(
                Date.UTC(nextStart.getUTCFullYear(), 11, 31)
              );

              newPolicy.fromDate = nextStartStr;
              newPolicy.toDate = nextEnd.toISOString().slice(0, 10);
            }
            onChange([...policies, newPolicy]);
          }}
          className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm"
        >
          <Plus size={15} />
          Add Period
        </button>
      </div>

      {policies.length === 0 && (
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No policies configured.
        </p>
      )}

      {policies.map((policy, pIdx) => {
        const started = hasStarted(policy);
        const lockedDefaultStart =
          pIdx === 0 && policy.fromDate.endsWith('-01-01');
        const isLast = pIdx === policies.length - 1;
        return (
          <div
            key={pIdx}
            className={`p-4 rounded-lg border space-y-4 ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}
          >
            <div className="flex items-start gap-3">
              <div className="grid grid-cols-2 gap-3 flex-1">
                <div>
                  <label className={labelClassName}>From Date</label>
                  <input
                    type="date"
                    value={policy.fromDate}
                    disabled={started || lockedDefaultStart}
                    onChange={(e) => updatePolicy(pIdx, { fromDate: e.target.value })}
                    className={`${inputClassName} ${started || lockedDefaultStart ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  {(started || lockedDefaultStart) && (
                    <p className="text-xs text-amber-600 mt-0.5">
                      {started ? 'Started — date locked' : 'Default Jan 1 start — date locked'}
                    </p>
                  )}
                </div>
                <div>
                  <label className={labelClassName}>To Date</label>
                  <input
                    type="date"
                    value={policy.toDate}
                    disabled={!isLast}
                    onChange={(e) => updatePolicy(pIdx, { toDate: e.target.value })}
                    className={`${inputClassName} ${!isLast ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  {!isLast && (
                    <p className="text-xs text-gray-500 mt-0.5">Auto-set to day before next period</p>
                  )}
                </div>
              </div>
              {!started && (
                <button
                  type="button"
                  onClick={() => onChange(policies.filter((_, i) => i !== pIdx))}
                  className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  title="Remove period"
                >
                  <Trash2 size={17} />
                </button>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Leave Types (days / year)
                </span>
                <button
                  type="button"
                  onClick={() =>
                    updatePolicy(pIdx, {
                      leaveTypes: [...policy.leaveTypes, { name: '', allocated: '' }],
                    })
                  }
                  className="text-xs px-2 py-1 text-indigo-600 border border-indigo-300 rounded hover:bg-indigo-50 transition-colors"
                >
                  + Add Type
                </button>
              </div>

              {policy.leaveTypes.map((lt, ltIdx) => (
                <div key={ltIdx} className="flex items-center gap-2 w-full">
                  <input
                    type="text"
                    value={lt.name}
                    placeholder="e.g. privilege"
                    onChange={(e) => updateLeaveType(pIdx, ltIdx, { name: e.target.value })}
                    className={`${inputClassName} !w-auto flex-1 min-w-0`}
                  />
                  <input
                    type="number"
                    value={lt.allocated}
                    placeholder="days"
                    min="0"
                    step="any"
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    onChange={(e) =>
                      updateLeaveType(pIdx, ltIdx, {
                        allocated: e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                    className={`${inputClassName} !w-24 shrink-0`}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      updatePolicy(pIdx, {
                        leaveTypes: policy.leaveTypes.filter((_, i) => i !== ltIdx),
                      })
                    }
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LeavePoliciesEditor;
